import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';

import { Button } from '@/components';
import { type AnchoredPanelPosition, computeAnchoredPanelPosition } from '@/lib/anchored-panel';
import { cn } from '@/lib/utils';

import { FONT_FAMILIES } from '../styleConstants';
import { ensureFontLoaded } from '../styleToCSS';

interface FontFamilyPickerProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}

export function FontFamilyPicker({ value, onChange }: FontFamilyPickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [panelPos, setPanelPos] = useState<AnchoredPanelPosition | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Panel jest w PORTALU (poza dropdownRef) — inaczej `position:absolute` przycina
  // rodzic z overflow-hidden (sekcja akordeonu w StylePanelAdvanced, §4.12).
  useEffect(() => {
    if (!isOpen) {
      setPanelPos(null);
      return;
    }
    const update = () => {
      const el = dropdownRef.current;
      if (!el) return;
      setPanelPos(computeAnchoredPanelPosition(el.getBoundingClientRect()));
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = dropdownRef.current?.contains(target) ?? false;
      const inPanel = panelRef.current?.contains(target) ?? false;
      if (!inTrigger && !inPanel) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Preload fonts when dropdown opens
  useEffect(() => {
    if (isOpen) {
      FONT_FAMILIES.forEach((f) => ensureFontLoaded(f.value));
    }
  }, [isOpen]);

  const selectedFont = FONT_FAMILIES.find((f) => f.value === value);
  const defaultLabel = t('tenant.systemModuleBuilder.style.typography.defaultFont');

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        type="button"
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full justify-between rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-normal text-gray-900 hover:border-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        style={selectedFont ? { fontFamily: selectedFont.preview } : undefined}
      >
        <span className="truncate">{selectedFont?.label || defaultLabel}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`}
        />
      </Button>

      {isOpen &&
        panelPos &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              top: panelPos.top,
              left: panelPos.left,
              width: panelPos.width,
              maxHeight: panelPos.maxHeight,
              zIndex: 60,
            }}
            className="overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
          >
            {/* Clear option */}
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                onChange(undefined);
                setIsOpen(false);
              }}
              className={cn(
                'w-full justify-start gap-2 rounded-none px-3 py-2 text-left text-xs font-normal hover:bg-gray-50 dark:hover:bg-gray-700',
                !value
                  ? 'text-accent-600 bg-accent-50 dark:bg-accent-900/20'
                  : 'text-gray-600 dark:text-gray-400',
              )}
            >
              {!value && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
              <span className={!value ? '' : 'pl-5'}>{defaultLabel}</span>
            </Button>

            {FONT_FAMILIES.map((font) => (
              <Button
                key={font.value}
                type="button"
                variant="ghost"
                onClick={() => {
                  onChange(font.value);
                  setIsOpen(false);
                }}
                className={cn(
                  'w-full justify-start gap-2 rounded-none px-3 py-2 text-left text-xs font-normal hover:bg-gray-50 dark:hover:bg-gray-700',
                  value === font.value
                    ? 'text-accent-600 bg-accent-50 dark:bg-accent-900/20'
                    : 'text-gray-900 dark:text-gray-100',
                )}
                style={{ fontFamily: font.preview }}
              >
                {value === font.value && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                <span className={value === font.value ? '' : 'pl-5'}>{font.label}</span>
              </Button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
